<template>
  <LogTimeReportFormModal v-model="show" :title="titleText" id="time-off-form-modal" :loading="isModalLoading || getIsModalLoading">
    <FormGenerator ref="log-time-form" class="form-generator-container" :data="form.fields"
      :textButtonSubmit="submitButtonText" :showReset="false"
      :showFormControls="(isSaveMode && !isInDisableMode) || isApprovalMode" @submit="submit"
      :isShowSubmitBtn="!isApprovalMode" @input="changeFormFields($event)">
      <template v-slot:user="slotProps">
        <b-form-group>
          <label class="form__label">{{ slotProps.data.label }}</label>
          <multiselect v-model="slotProps.data.value" :options="slotProps.data.options"
            :track-by="slotProps.data.valueField" :label="slotProps.data.selectLabel" :name="slotProps.data.name"
            :disabled="true" :showLabels="false" allow-empty />
        </b-form-group>
      </template>
      <template v-slot:AttachmentFile="slotProps">
        <label class="form__label">{{ slotProps.data.label }}</label>
        <div class="row">
          <div class="col">
            <b-form-group>
              <b-input-group>
                <b-form-file ref="attachmentFile" v-model="slotProps.data.value" :disabled="isInDisableMode"
                  :name="slotProps.data.name" class="attachment-file"
                  :placeholder="slotProps.data.value && dataProps.file_name ? dataProps.file_name : 'Choose attachment file'"
                  drop-placeholder="Drop file here...">
                </b-form-file>
                <template #append>
                  <b-input-group-text class="attachment-icon" @click="triggerFileInput">
                    <span class="material-icons">attachment</span>
                  </b-input-group-text>
                </template>
              </b-input-group>
            </b-form-group>
          </div>
          <div class="col col-md-1 my-auto" v-if="dataProps.file_name">
            <a target="_blank" :href="dataProps.download_url" :disabled="!dataProps.download_url"
              :title="dataProps.file_name">
              <font-awesome-icon icon="file-invoice" size="2x" />
            </a>
          </div>
        </div>
      </template>
      <template v-slot:comment="slotProps">
        <b-form-group>
          <div v-for="({ type, text }, index) in validationMessages" :key="index">
            <div class="alert" :class="'alert-' + type">{{ text }}</div>
          </div>
        </b-form-group>
        <b-form-group>
          <label class="form__label">{{ slotProps.data.label }}</label>
          <b-form-textarea id="textarea" v-model="slotProps.data.value" :name="slotProps.data.name" rows="3"
            max-rows="6" :disabled="isInDisableMode">
          </b-form-textarea>
        </b-form-group>
      </template>
      <template v-slot:LoginToJira>
        <LogTimeLoginToJira v-if="isProjectConnectedToJira && !isJiraAuth">
          <template #text>
            To log time to this task you need to log in to Jira
          </template>
        </LogTimeLoginToJira>
      </template>
      <template v-slot:form-footer-left-controllers>
        <div v-if="!isApprovalMode" class="d-flex align-items-center" style="width: -webkit-fill-available;">
          <span class="material-symbols-outlined info-icon">
            info
          </span>
          <template v-if="isPayable">
            This is payable time off
          </template>
          <template v-else>
            This is not payable time off
          </template>
        </div>
      </template>
    </FormGenerator>
    <div :class="{ 'info-container': !isApprovalMode, 'view-container': titleText === 'View time off' }" class="d-flex">
      <div v-if="isApprovalMode" class="d-flex align-items-center">
        <span class="material-symbols-outlined info-icon">
          info
        </span>
        <template v-if="isPayable">
          This is payable time off
        </template>
        <template v-else>
          This is not payable time off
        </template>
      </div>
      <div class="ml-auto mr-2">
        <div v-if="isApproved">
          <font-awesome-icon icon="check-square" size="1x" />
          Approved by {{ approverInfo.name }} at {{ approverInfo.date }}
        </div>
        <div v-if="isPending && !isApprovalMode">
          <font-awesome-icon icon="question-circle" size="1x" />
          Pending approval
        </div>
      </div>
      <template v-if="isApprovalMode">
        <b-button class="button mr-2 approve-buttons" variant="danger" :disabled="isApproved" @click="decline">Decline
        </b-button>
        <b-button class="button approve-buttons" variant="success" :disabled="isApproved" @click="approve">Approve
        </b-button>
      </template>
    </div>
  </LogTimeReportFormModal>
</template>

<script>
import { cloneDeep, debounce } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import Moment from 'moment/moment'
import FormGenerator from '@components/FormGenerator'
import LogTimeReportFormModal from '@components/log-time-report/LogTimeReportFormModal.vue'
import LogTimeLoginToJira from '@components/log-time-report/LogTimeLoginToJira.vue'
import Global from '@/mixins/Global'

export default {
  name: 'TimeOffFormModal',
  mixins: [Global],
  components: {
    FormGenerator,
    LogTimeReportFormModal,
    LogTimeLoginToJira
  },
  props: {
    mode: {
      type: String,
      default: 'create'
    },
    dataProps: {
      type: Object
    },
    value: {
      type: Boolean
    },
    isModalLoading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    const defaultDateFormat = 'YYYY-MM-DD'
    const defaultForm = {
      fields: {
        'id': {
          name: 'id',
          value: null,
          class: 'd-none'
        },
        'user': {
          type: 'slot',
          slotName: 'user',
          name: 'user_id',
          label: 'User',
          selectLabel: 'name',
          valueField: 'id',
          options: [],
          disabled: true,
          value: null,
          class: 'col-6 field-container'
        },
        'time_offs_setting': {
          type: 'select',
          name: 'time_offs_settings_id',
          label: 'Time off type',
          selectLabel: 'name',
          valueField: 'id',
          options: [],
          value: null,
          class: 'col-6 field-container'
        },
        'date_from': {
          type: 'datepicker',
          name: 'date_from',
          label: 'Date from',
          validation: [
            { name: 'required' }
          ],
          value: Moment().subtract(1, 'months').startOf('month').format(defaultDateFormat),
          format: 'yyyy-MM-dd',
          class: 'col-md-3 field-container',
          group: 'filters'
        },
        'date_to': {
          type: 'datepicker',
          name: 'date_to',
          label: 'Date to',
          validation: [
            { name: 'required' }
          ],
          value: Moment().subtract(1, 'months').endOf('month').format(defaultDateFormat),
          format: 'yyyy-MM-dd',
          class: 'col-md-3 field-container',
          group: 'filters'
        },
        'period': {
          type: 'slot',
          slotName: 'period1',
          class: 'd-none'
        },
        'attachment_file': {
          type: 'slot',
          slotName: 'AttachmentFile',
          name: 'attachment_file',
          label: 'Attachment file',
          value: null,
          fileInfo: null,
          class: 'col-6 field-container'
        },
        'comment': {
          type: 'slot',
          slotName: 'comment',
          name: 'comment',
          label: 'Comment',
          placeholder: 'Comment',
          value: '',
          class: 'col-12'
        },
        'login_to_jira': {
          type: 'slot',
          slotName: 'LoginToJira',
          class: 'col-12'
        }
      }
    }

    return {
      defaultDateFormat,
      defaultForm,
      form: cloneDeep(defaultForm),
      validationMessages: []
    }
  },
  computed: {
    ...mapGetters({
      getIsModalLoading: 'finances/getIsModalLoading',
      getAllUsers: 'getAllUsersWithTrashed',
      timeOffsSettings: 'timeOffsSettings/getTimeOffsSettings'
    }),
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    isApproved () {
      return this.dataProps.status === 'approved'
    },
    isPending () {
      return this.dataProps.status === 'pending'
    },
    isProjectConnectedToJira () {
      const selectedTimeOffSetting = this.form.fields.time_offs_setting?.value
      if (!selectedTimeOffSetting || !selectedTimeOffSetting.logging_task || !selectedTimeOffSetting.logging_task.project) {
        return false
      }
      // Show Jira login if the logging task uses a Jira project (not custom project)
      return selectedTimeOffSetting.logging_task.project.is_custom_project !== 1
    },
    approverInfo () {
      if (this.dataProps.time_off_request) {
        return {
          'name': this.dataProps.time_off_request.approver.name,
          'date': Moment(this.dataProps.time_off_request.approver.updated_at).format('YYYY-MM-DD HH:mm')
        }
      } else {
        return {
          'name': '',
          'date': ''
        }
      }
    },
    isApprovalMode () {
      return this.mode === 'approve'
    },
    isSaveMode () {
      return this.mode === 'create' || this.mode === 'edit'
    },
    isInDisableMode () {
      return this.isApprovalMode || (this.form.fields.user.value && this.form.fields.user.value.id !== this.getUser.id)
    },
    submitButtonText () {
      return this.mode === 'edit' ? 'Save' : 'Create'
    },
    titleText () {
      if (this.isApprovalMode) {
        const isEdited = this.dataProps.created_at !== this.dataProps.updated_at
        return 'Approve time off' + (isEdited ? ' (last updated at ' + Moment(this.dataProps.updated_at).format('YYYY-MM-DD HH:mm') + ')' : '')
      } else if (this.mode === 'create') {
        return 'Add time off'
      } else if (this.mode === 'edit') {
        return 'Edit time off'
      } else {
        return 'View time off'
      }
    },
    isPayable () {
      return this.form.fields.time_offs_setting.value && this.form.fields.time_offs_setting.value.is_payable
    }
  },
  watch: {
    'value': {
      deep: true,
      handler () {
        if (this.value && !this.dataProps) {
          this.resetForm()
        }
      }
    }
  },
  async mounted () {
    this.setIsModalLoading(false)
    await this.setInitialForm()
    if (Object.keys(this.dataProps).length > 0) {
      this.form.fields.id.value = this.dataProps.id ?? this.form.fields.id.value
      this.form.fields.user.value = this.dataProps.user ?? this.form.fields.user.value
      this.form.fields.time_offs_setting.value = this.dataProps.time_offs_settings ?? this.form.fields.time_offs_setting.value
      this.form.fields.date_from.value = this.dataProps.date_from ?? this.form.fields.date_from.value
      this.form.fields.date_to.value = this.dataProps.date_to ?? this.form.fields.date_to.value
      this.form.fields.attachment_file.value = this.dataProps.attachment_file ?? this.form.fields.attachment_file.value
      this.form.fields.comment.value = this.dataProps.comment ?? this.form.fields.comment.value
    }

    if (this.isInDisableMode) {
      for (let name in this.form.fields) {
        this.form.fields[name].disabled = true
      }
    }
  },
  methods: {
    ...mapActions({
      setIsModalLoading: 'finances/setIsModalLoading'
    }),
    async fetchTimeOffsSettings () {
      return this._toggleDataPageLoading(() => this.$api.timeOffsSettings.getTimeOffsSettings())
    },
    triggerFileInput () {
      this.$refs.attachmentFile.$refs.input.click()
    },
    resetForm () {
      this.form = cloneDeep(this.defaultForm)
      this.setInitialForm()
    },
    async setInitialForm () {
      const needsToLoadData = !this.getAllUsers.length || !this.timeOffsSettings.length

      if (needsToLoadData) {
        this.setIsModalLoading(true)
      }

      try {
        if (!this.getAllUsers.length) {
          await this.$api.users.getAllUsers()
        }
        if (!this.timeOffsSettings.length) {
          await this.fetchTimeOffsSettings()
        }
      } finally {
        if (needsToLoadData) {
          this.setIsModalLoading(false)
        }
      }
      this.form.fields.id.value = null
      this.form.fields.user.options = this.getAllUsers
      this.form.fields.user.value = this.getAllUsers.find(user => user.id === this.getUser.id)
      this.form.fields.time_offs_setting.options = this.timeOffsSettings
      this.form.fields.time_offs_setting.value = this.timeOffsSettings.length ? this.timeOffsSettings[0] : null
      this.form.fields.date_from.value = Moment().format(this.defaultDateFormat)
      this.form.fields.date_to.value = Moment().format(this.defaultDateFormat)
      this.form.fields.attachment_file.value = null
      this.form.fields.comment.value = ''
    },
    async submit (fields) {
      const data = this.prepareDataForRequest(fields)

      if (this.$refs['attachmentFile'] && this.$refs['attachmentFile'].value) {
        const attachmentFileBase64 = await this.fileToBase64(this.$refs['attachmentFile'].value)
        data['attachment_file'] = attachmentFileBase64
      } else {
        data['attachment_file'] = null
      }

      this.$emit('save', data)
    },
    approve () {
      this.$emit('approve')
    },
    decline () {
      this.$emit('decline')
    },
    prepareDataForRequest (fields) {
      let newFields = cloneDeep(fields)
      let data = {}

      for (let key in newFields) {
        const field = newFields[key]
        const name = field['name']
        let value = field['value']
        if (!value) {
          continue
        }
        if (name === 'date_from' || name === 'date_to') {
          value = this._toLocalTime(value)
        }
        if (value) {
          data[name] = typeof value === 'object' ? value[field['valueField']] : value
        }
      }
      return data
    },
    hideModal () {
      this.setIsModalLoading(false)
    },
    changeFormFields (event) {
      if (this.isModalLoading || this.getIsModalLoading) return
      this.form.fields = event
      const fields = this.prepareDataForRequest(this.form.fields)
      if (fields.time_offs_settings_id && fields.user_id && fields.date_from && fields.date_to) {
        this.validate(fields)
      }
    },
    validate: debounce(function (data) {
      if (this.isInDisableMode) return
      this.$api.timeOffs.validateTimeOff(data)
        .then(({ data, status }) => {
          this.validationMessages = []
        })
        .catch(error => {
          this.validationMessages = []

          if (error.response.data.errors) {
            const errors = Object.values(error.response.data.errors)
            for (let i = 0; i < errors.length; i++) {
              const errorText = Array.isArray(errors[i]) ? errors[i][0] : errors[i]
              this.validationMessages.push({ type: 'danger', text: errorText })
            }
          }

          if (error.response.data.warnings) {
            const warnings = Object.values(error.response.data.warnings)
            for (let j = 0; j < warnings.length; j++) {
              const warningText = Array.isArray(warnings[j]) ? warnings[j][0] : warnings[j]
              this.validationMessages.push({ type: 'warning', text: warningText })
            }
          }
        })
    }, 500)
  }
}
</script>

<style lang="scss" scoped>
::v-deep .field-container {
  margin-bottom: 4px;
}

.info-icon {
  font-size: 20px;
  margin-right: 8px;
}

  .commission-column {
    max-width: 200px;
  }
  .total-amount {
    float:right;
    font-size: x-large;
  }
  .fetch-tasks-spinner {
    position:absolute;
    top:40px;
    left:15px;
    z-index:1;
  }
  .form-generator {
    label {
    font-size: 12px;
    color: $inputLabelColor1;
  }
}

.attachment-icon {
  height: 32px;
  background: #f3f5f7;
  cursor: pointer;
}

.approve-buttons {
  justify-content: center;
  display: flex;
  width: 180px;
}

.info-container {
  width: 100%;
  // position: absolute;
  // margin-top: -32px;
  // width: calc(100% - $form-generator-button-width - 5px);
}

.view-container {
  position: unset;
  margin-top: 20px;
  margin-left: -8px;
  width: 100%;
}

.alert-warning {
  color: $fontColorBase;
}
</style>
